import React, { useState, useEffect, useCallback, useMemo, memo } from 'react'
import { Article } from './components/Article'
import { translate } from './util/translate'
import { FilterMenu } from './components/FilterMenu'
import queryString from 'query-string'
import { Searchbar } from './components/Searchbar'
import { GroupHeader } from './components/GroupHeader'
import debounce from 'lodash.debounce'
import { TabBar } from './components/TabBar'

const queryStringOptions = { arrayFormat: 'bracket' } as const

const fetchPage = async (
  page: number,
  args: {
    itemsPerPage?: number
    query?: string
    filters?: Array<{ key: string; items: number[] }>
  } = {}
) => {
  const params = {
    page,
    itemsPerPage: args.itemsPerPage ?? 5,
    query: args.query ?? '',
    filters: args.filters ?? []
  }

  const originalQueryParams = queryString.parse(window.location.search, queryStringOptions)

  const newQuery = queryString.stringify(
    {
      ...originalQueryParams,
      query: args.query,
      filters: args.filters?.filter((f) => f.key !== 'type').flatMap((f) => f.items) ?? [],
      type: args.filters?.find((f) => f.key === 'type')?.items ?? []
    },
    queryStringOptions
  )

  window.history.replaceState(
    undefined,
    '',
    window.location.protocol + '//' + window.location.host + window.location.pathname + '?' + newQuery
  )

  const response = await fetch((window as any).endpoint ?? `/umbraco/surface/SupportSite/ArticleSearch`, {
    method: 'POST',
    body: JSON.stringify(params)
  })

  return response.json()
}

type ArticleFromServer = {
  date: string
  title: string
  intro: string | null
  link: string
  loginRequired: boolean
  additionalInfo: string
}

type ResultGroupFromServer = {
  title: string
  results: Array<ArticleFromServer>
  total: number
  filter: { key: string; value: number }
}

export type FilterFromServer = {
  name: string
  options: Array<{ id: number; name: string }>
}

export const App = () => {
  const parsedQS = queryString.parse(window.location.search, queryStringOptions)
  const [isLoading, setLoading] = useState(true)
  const [isInlineLoading, setInlineLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [currentPageIndex, setCurrentPageIndex] = useState(0)
  const [results, setResults] = useState<Array<ResultGroupFromServer>>([])
  const [initialized, setInitialized] = useState(false)
  const [typeFilter, setTypeFilter] = useState<FilterFromServer>({ name: 'type', options: [] })
  const [filters, setFilters] = useState<Array<FilterFromServer>>([])
  const [activeFilters, setActiveFilters] = useState<number[]>(
    ((parsedQS.filters as string[]) ?? []).map((x) => parseInt(x, 10))
  )
  const [activeTypeFilter, setActiveTypeFilter] = useState<number | undefined>(
    parsedQS.type == null ? undefined : parseInt(parsedQS.type as string)
  )
  const [query, setQuery] = useState<string>((parsedQS.query as string) ?? '')

  const totalResults = results.reduce((acc, cur) => acc + cur.total, 0)

  const filtersToApply = useMemo(
    () =>
      Object.entries(
        activeFilters.reduce<Record<string, number[]>>(
          (acc, cur) => {
            const group = filters.find((f) => f.options.find((o) => o.id === cur) !== undefined)?.name ?? 'type'
            const currentFiltersForGroup = (Reflect.get(acc, group) ?? []) as number[]

            return {
              ...acc,
              [group]: [...currentFiltersForGroup, cur]
            }
          },
          {}
          //activeTypeFilter === undefined ? {} : { type: [activeTypeFilter] }
        )
      ).map(([key, items]) => ({ key, items })),
    [activeFilters, filters]
  )

  const fetchFirstPage = useCallback(
    debounce((_filters: typeof filters, _activeFilters: typeof activeFilters, _query: typeof query) => {
      setLoading(true)
      setResults([])
      fetchPage(0, {
        query: _query,
        filters: filtersToApply
      })
        .then((page) => {
          setLoading(false)
          setCurrentPageIndex(1)
          setResults(page.resultGroups)
          setFilters(page.filters)
          setTypeFilter({ name: 'type', options: page.types ?? [] })
          setInitialized(true)
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.log('error', e)
          setHasError(true)
        })
    }, 1000),
    []
  )

  const applyFilters = useCallback(
    debounce(
      (
        _filters: typeof filters,
        _activeFilters: typeof activeFilters,
        _query: typeof query,
        _filtersToApply: typeof filtersToApply,
        _activeTypeFilter: typeof activeTypeFilter
      ) => {
        setLoading(true)
        setResults([])
        fetchPage(0, {
          query: _query,
          filters: [
            ..._filtersToApply,
            ...(_activeTypeFilter === undefined
              ? []
              : [
                  {
                    key: 'type',
                    items: [_activeTypeFilter]
                  }
                ])
          ]
        })
          .then((page) => {
            setLoading(false)
            setCurrentPageIndex(1)
            setResults(page.resultGroups)
            setFilters(page.filters)
            setInitialized(true)
          })
          .catch((e) => {
            // eslint-disable-next-line no-console
            console.log('error', e)
            setHasError(true)
          })
      },
      1000
    ),
    []
  )

  const fetchNextPage = useCallback(() => {
    setInlineLoading(true)
    setCurrentPageIndex(currentPageIndex + 1)
    fetchPage(0, {
      itemsPerPage: (currentPageIndex + 1) * 5,
      query: query,
      filters: [
        ...filtersToApply,
        ...(activeTypeFilter === undefined
          ? []
          : [
              {
                key: 'type',
                items: [activeTypeFilter]
              }
            ])
      ]
    })
      .then((page) => {
        setResults(page.resultGroups)
        setInlineLoading(false)
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.log('error', e)
        setHasError(true)
      })
  }, [activeTypeFilter, currentPageIndex, filtersToApply, query])

  useEffect(
    () => {
      if (!initialized) {
        fetchFirstPage(filters, activeFilters, query)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(
    () => {
      if (initialized) {
        applyFilters(filters, activeFilters, query, filtersToApply, activeTypeFilter)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [applyFilters, activeFilters, query]
  )

  const clearAll = useCallback(() => {
    setActiveFilters([])
  }, [setActiveFilters])

  const onSeeMore = useCallback(
    (filterId: number) => {
      setActiveTypeFilter(filterId)
      applyFilters(filters, activeFilters, query, filtersToApply, filterId)
    },
    [activeFilters, applyFilters, filters, filtersToApply, query]
  )

  // wait for the first web request, st. we may render the filter menu without it jumping
  if (initialized === false) {
    return <></>
  }

  return (
    <div className="grid-outer-container grid-gap2-above">
      <div className="grid-row">
        <div className="grid-span12 grid-gap1-below">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              setLoading(true)
              applyFilters(filters, activeFilters, query, filtersToApply, activeTypeFilter)
              return false
            }}
          >
            <Searchbar
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={translate('Search Articles and Documentation')}
            />
          </form>
        </div>
      </div>

      <div className="grid-row">
        <div className="grid-span3 grid-tablet-hidden" style={{ paddingRight: '18px' }}>
          <FilterMenu
            filters={filters}
            activeFilters={activeFilters}
            setActiveFilters={setActiveFilters}
            clearAll={clearAll}
          />
        </div>
        <div className="grid-span9 grid-tablet-span12">
          <div className="grid-hidden grid-tablet-visible grid-gap3-below">
            <FilterMenu
              filters={filters}
              activeFilters={activeFilters}
              setActiveFilters={setActiveFilters}
              clearAll={clearAll}
            />
          </div>

          <Content
            hasError={hasError}
            isLoading={isLoading}
            isInlineLoading={isInlineLoading}
            totalResults={totalResults}
            results={results}
            onSeeMore={onSeeMore}
            fetchNextPage={() => fetchNextPage()}
          >
            {typeFilter.options.length > 0 && (
              <TabBar
                filter={typeFilter}
                activeFilter={activeTypeFilter}
                setActiveFilter={(newActiveTypeFilter) => {
                  setActiveFilters([])
                  setActiveTypeFilter(newActiveTypeFilter)
                  applyFilters(filters, [], query, filtersToApply, newActiveTypeFilter)
                }}
              />
            )}
          </Content>
        </div>
      </div>
    </div>
  )
}

type ContentProps = {
  hasError: boolean
  isLoading: boolean
  isInlineLoading: boolean
  totalResults: number
  results: ResultGroupFromServer[]
  onSeeMore: (filterId: number) => void
  fetchNextPage: () => void
}

const Content: React.FC<ContentProps> = memo(
  ({ hasError, isLoading, totalResults, results, onSeeMore, fetchNextPage, isInlineLoading, children }) => (
    <>
      {children}

      {hasError ? (
        <div>
          <h3 className="grid-text-center grid-gap5-above grid-gap5-below">{translate('Error - try again later')}</h3>
        </div>
      ) : isLoading ? (
        <div>
          <h3 className="grid-text-center dots grid-gap5-above grid-gap5-below">
            {translate('Loading')}
            <span>.</span>
            <span>.</span>
            <span>.</span>
            <p></p>
          </h3>
        </div>
      ) : totalResults === 0 ? (
        <div>
          <h3 className="grid-text-center grid-gap5-above grid-gap5-below">{translate('No results')}</h3>
        </div>
      ) : (
        <>
          {results.map((group) => (
            <div key={group.title} style={{ paddingBottom: '24px' }}>
              {results.length > 1 ? (
                <GroupHeader
                  title={group.title}
                  pagination={`${group.results.length} ${translate('out of')} ${group.total} ${
                    group.total === 0 ? translate('result') : translate('results')
                  }`}
                  onSeeMore={group.total <= group.results.length ? undefined : () => onSeeMore(group.filter.value)}
                />
              ) : children !== false ? (
                <div>&nbsp;</div>
              ) : (
                <></>
              )}

              {group.results.map((article, i) => (
                <Article
                  key={i}
                  link={article.link}
                  date={article.date}
                  title={article.title}
                  intro={article.intro}
                  additionalInfo={article.additionalInfo}
                  loginRequired={article.loginRequired === true}
                />
              ))}

              {isInlineLoading && (
                <div>
                  <h3 className="grid-text-center dots grid-gap3-above grid-gap3-below">
                    {translate('Loading')}
                    <span>.</span>
                    <span>.</span>
                    <span>.</span>
                    <p></p>
                  </h3>
                </div>
              )}

              {results.length === 1 && group.total > group.results.length && (
                <div className="grid-text-center grid-gap2-above">
                  <button className="button" onClick={() => fetchNextPage()}>
                    {translate('Load more results')}
                  </button>
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </>
  )
)
